import { useTranslation } from 'react-i18next'
import { formatDate, formatDateTime, formatTime } from '../../../lib/datetime'
import ActionStatusTag from './ActionStatusTag'
import type { ActionDetail } from '../types'

/**
 * Aksiyon Bilgileri ızgarası (7.6) — Aksiyon Numarası · (toplantıya bağlıysa) Toplantı Adı/Yer/Zaman
 * (+ ana görevde Tutanağı Düzenleyen) ·
 * Hedef Teslim Tarihi · Önem · Durum.
 *
 * <p><b>Tamamlanma Notu burada DEĞİLDİR</b> ({@code ActionCompletionNote}): 2.000 karaktere kadar
 * çıkabilen serbest metin, ~220px'lik bir ızgara gözüne sığmıyordu — kartın altında kendi bölümünde
 * durur (kullanıcı kararı 2026-09-16). Görev Detay sayfasının JSX'inden ÇIKARILMIŞTI: buradaki koşullu hücreler
 * sayfanın bilişsel karmaşıklığının ölçülen en büyük tek kalemiydi (Sonar S3776).
 *
 * <p><b>2026-09-10'da ayrı dosyaya taşındı</b> — "Ana Görevi Gör" popup'ı da aynı ızgarayı çizer
 * ({@code MainTaskViewDialog}). Kopyalanmadı: sayfa ile popup aynı görevin aynı alanlarını gösterir
 * ve bir hücre eklendiğinde (ör. Kapanma Tarihi) ikisinin ayrışması, kullanıcının aynı kaydı iki
 * yerde farklı görmesi demek olurdu.
 *
 * <p>Salt-okumadır: hiçbir girdi/kontrol çizmez, yalnız {@code <dl>} hücreleri. Bu yüzden popup
 * tarafında ayrıca bir "read-only" bayrağı gerekmez.
 */
export default function ActionInfoGrid({
  data,
  meetingBound,
  urgencyName,
  importanceName,
  statusId,
}: Readonly<{
  data: ActionDetail | undefined
  meetingBound: boolean
  urgencyName: string
  importanceName: string
  statusId: number | null
}>) {
  const { t } = useTranslation()
  return (
    <dl className="mtg-info-grid">
      <div>
        <dt>{t('actions.columns.number')}</dt>
        <dd>{data?.recordNumber ?? ''}</dd>
      </div>
      {meetingBound && (
        <>
          <div>
            <dt>{t('actions.detail.info.meeting')}</dt>
            <dd>{data?.meetingName ?? ''}</dd>
          </div>
          <div>
            <dt>{t('actions.detail.info.location')}</dt>
            <dd>{data?.meetingLocation ?? ''}</dd>
          </div>
          <div>
            <dt>{t('actions.detail.info.time')}</dt>
            <dd>{meetingDateTimeRange(data)}</dd>
          </div>
          {/* Tutanağı Düzenleyen — yalnız ana görevde (sunucu alt görevde null döner). */}
          {data?.parentId == null && (
            <div>
              <dt>{t('actions.detail.info.minutesEditor')}</dt>
              <dd>{data?.minutesEditorName ?? '—'}</dd>
            </div>
          )}
        </>
      )}
      <div>
        <dt>{t('actions.form.deadline')}</dt>
        <dd>{data?.deadline ? formatDate(data.deadline) : ''}</dd>
      </div>
      <div>
        <dt>{t('actions.form.urgency')}</dt>
        <dd>{urgencyName}</dd>
      </div>
      <div>
        <dt>{t('actions.form.importance')}</dt>
        <dd>{importanceName}</dd>
      </div>
      <div>
        <dt>{t('actions.columns.status')}</dt>
        <dd>
          {/* "Gecikti" etiketi buradan BÖLÜM BAŞLIĞINA taşındı (kullanıcı isteği 2026-08-14) — aynı
              ekranda iki kez çizilmesin diye burada tekrarlanmıyor. */}
          <ActionStatusTag statusId={statusId} name={data?.statusName ?? ''} />
        </dd>
      </div>
      {/* Görevi oluşturan (CRE_BY) — sunucu adı çözer; kayıt yoksa "#id" döner. */}
      <div>
        <dt>{t('actions.detail.info.createdBy')}</dt>
        <dd>{data?.createdByName ?? '—'}</dd>
      </div>
      {/* Kapanma Tarihi — yalnız kapatılmış aksiyonda (CLOSURE_DATE, statü Kapatıldı'ya çekilince yazılır). */}
      {data?.closureDate && (
        <div>
          <dt>{t('actions.detail.info.closureDate')}</dt>
          <dd>{formatDate(data.closureDate)}</dd>
        </div>
      )}
    </dl>
  )
}

/**
 * Toplantının tarihi + saat aralığı ("05.08.2026 14:00 - 15:30"); bitiş yoksa yalnız başlangıç saati,
 * tarih yoksa boş. Toplantı detayı tarihi ve saati AYRI iki hücrede gösterir; görev detayında tek bir
 * "Zaman" hücresi olduğu için ikisi burada birleştirilir (kullanıcı isteği 2026-09-01) — daha önce
 * yalnız saat yazıyordu ve görev, hangi güne ait bir toplantıdan geldiğini söylemiyordu.
 *
 * <p>Bitişin tarihi yazılmaz: toplantı formu tek tarih + saat aralığı alır, yani başlangıç ve bitiş
 * her zaman aynı gündedir.
 */
function meetingDateTimeRange(data: ActionDetail | undefined) {
  if (!data?.meetingDate) return ''
  const end = data.meetingEndDate ? ` - ${formatTime(data.meetingEndDate)}` : ''
  return `${formatDateTime(data.meetingDate)}${end}`
}
